import {
    deleteDoc,
    type DocumentReference,
    type Timestamp,
} from 'firebase/firestore';
import { type FullMetadata, getMetadata } from 'firebase/storage';
import { reaction } from 'mobx';

import {
    getHostingStorageFileRef,
    hostingStoragePathToUrl,
} from 'services/Firebase';
import { AssetFileType } from 'types/AssetFileType';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentStatus } from 'types/DocumentStatus';
import { DocumentType } from 'types/DocumentType';
import { FileTypeExtensions } from 'types/FileTypes';
import { ProcessingStatus } from 'types/ProcessingStatus';
import { nowTimestamp } from 'utils/FirestoreUtils';
import { getFilenameExtension, lastPathSegment } from 'utils/String';

import type AssetsManager from './Assets';
import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';

export class AssetSchema extends DocumentSchema {
    path: string = '';
    name: string = '';
    type: AssetFileType = AssetFileType.FILE;
    fileType: AssetFileType = AssetFileType.FILE;
    status: ProcessingStatus = ProcessingStatus.NONE;
    storage: string = '';
    dateModified: Timestamp = nowTimestamp();
    author: string = '';
    size: number = 0;
    mutex: number = 0;
    checksum: string = '';
}

export default class Asset extends DocumentStore<AssetSchema> {
    declare parent: AssetsManager | undefined;

    schema = new AssetSchema();
    type = DocumentType.ASSET;
    collectionType = DocumentCollectionType.ASSET;

    publicURL = '';
    default = false;
    bundleFilesize = NaN;
    backupImageFilesize = NaN;
    error = false;
    // Fetched lazily by updateFileSize(), so absent until that has run and
    // still absent if it threw. `get fileSize` already guarded for that, which
    // is what the type should have been saying. Initialized so
    // becomeObservable() stops skipping it.
    metadata: FullMetadata | undefined = undefined;
    fileSizeString = '';
    errorMessage = '';
    processing = false;

    get storageFilename() {
        return lastPathSegment(this.data.storage);
    }

    get filename() {
        if (!this.data) return '';
        return lastPathSegment(this.data.path);
    }

    get fileSize() {
        if (!this.metadata) return 0;
        return this.metadata.size;
    }

    get hostingRef() {
        return getHostingStorageFileRef(this.data.storage);
    }

    get loading() {
        // if(this.data.status === ProcessingStatus.NONE) return false;
        return this.data.status !== ProcessingStatus.COMPLETE;
    }

    constructor(parent: AssetsManager, docRef?: DocumentReference) {
        super(parent, docRef);
        // this.filename = this.getRootFromPath(this.data.path);
        this.becomeObservable();
        // Refresh the size whenever processing completes - on load, and again
        // if the asset is reprocessed (e.g. its file is replaced). `data` is
        // still empty here; the collection hydrates it via _update after
        // construction, and that first status change is what fires this.
        this.disposer.add(
            reaction(
                () => this.data.status,
                (status) => {
                    if (status === ProcessingStatus.COMPLETE) {
                        this.updateFileSize();
                    }
                }
            )
        );
    }

    get absoluteURL() {
        return this.downloadURL;
    }

    get storageFolder() {
        return this.data.storage.substring(
            0,
            this.data.storage.lastIndexOf('/') + 1
        );
    }

    get downloadURL() {
        return hostingStoragePathToUrl(this.data.storage);
    }

    onLoad = async (_data: AssetSchema) => {
        this.status = DocumentStatus.LOADING;
        this.data.name = lastPathSegment(this.data.path);
        this.status = DocumentStatus.LOADED;
    };

    onReady = async () => {
        // TODO: This data transform is currently needed due to a very early
        //  data structure conflict with Adhub 3.0 data schema standards. New
        //  schema standards use property 'type' on every document to define
        //  which schema type is being used for that document.
        this.data.fileType = String(this.data.type) as AssetFileType;
    };

    onBeforeSave = async (_data: AssetSchema) => {
        this.data.type = this.data.fileType;
    };

    delete = async () => {
        this.status = DocumentStatus.LOADING;
        // await this.hostingRef.delete(); // TODO: Cannot delete. Do not have access. Ask Jim/Backend for function to delete, or access to delete.
        await deleteDoc(this.docRef);
        this.status = DocumentStatus.LOADED;
    };

    updateFileSize = async () => {
        if (this.data.fileType !== AssetFileType.FOLDER) {
            try {
                this.metadata = await getMetadata(this.hostingRef);
                const sizeInKiB = this.metadata.size / 1024;
                this.fileSizeString = `${sizeInKiB.toFixed(2)} KB`;
            } catch (e) {
                console.log('Asset: Error getting file size', e);
            }
        }
    };

    updateName = async (newName: string) => {
        this.data.name = newName;
        await this.save();
    };

    setProcessing(isProcessing: boolean = true) {
        this.processing = isProcessing;
    }

    setLoading = (isLoading: boolean = true) => {
        if (isLoading) {
            this.data.status = ProcessingStatus.PENDING;
        } else {
            this.data.status = ProcessingStatus.COMPLETE;
        }
    };

    getFileType() {
        const ext = getFilenameExtension(this.filename) || '';
        if (FileTypeExtensions.IMAGE.includes(ext)) {
            return AssetFileType.IMAGE;
        } else if (FileTypeExtensions.VIDEO.includes(ext)) {
            return AssetFileType.VIDEO;
        } else if (FileTypeExtensions.AUDIO.includes(ext)) {
            return AssetFileType.AUDIO;
        } else if (FileTypeExtensions.FONT.includes(ext)) {
            return AssetFileType.FONT;
        } else if (FileTypeExtensions.TEXT.includes(ext)) {
            return AssetFileType.TEXT;
        } else {
            return AssetFileType.NONE;
        }
    }
}
